<template>
    <div class="flex size-full flex-col justify-stretch">
        <toast-portal />
        <router-view />
        <rate-limiting-modal />
        <vue-query-devtools />
    </div>
</template>

<script setup lang="ts">
import whiteLabelHelper from './api/helpers/whiteLabelHelper.js';
import {useEventBus} from '@vueuse/core';
import {storeToRefs} from 'pinia';
import {useAccountStore} from './stores/accountStore';
import {useGlobalStore} from './stores/globalStore';
import ToastPortal from './shared/components/toast/ToastPortal.vue';
import {useScriptLoader} from './shared/composables/useScriptLoader';
import {useRoute, useRouter} from 'vue-router';
import store from './store';
import {watch} from 'vue';
import RateLimitingModal from './shared/components/RateLimitingModal.vue';
import {useUserStore} from './stores/userStore.js';
import {setMatomoUserId} from '@/shared/matomoTracking';
import {VueQueryDevtools} from '@tanstack/vue-query-devtools';
import {useSubDomain} from '@/shared/composables/useSubdomain';
import {useWhitelabelSettingsQuery} from '@/api/queries/queries/useWhitelabelSettingsQuery';

useGlobalStore().init();
useScriptLoader();

const router = useRouter();
const route = useRoute();

const {account} = storeToRefs(useAccountStore());
const {user} = storeToRefs(useUserStore());

router.isReady().then(() => {
    let resourceType = 'seat';
    if (typeof route.name === 'string') {
        if (route.name.toLowerCase().includes('zone')) {
            resourceType = 'zone';
        }

        if (route.name.toLowerCase().includes('meetingroom')) {
            resourceType = 'meetingRoom';
        }
    }

    store.dispatch('resources/selectResource', resourceType);
});

watch(
    account,
    accountValue => {
        whiteLabelHelper.methods.whiteLabelCheck(accountValue);
        initializeResourceTypes();
    },
    {immediate: true}
);

useWhitelabelSettings();

watch(
    () => user.value?.id,
    userId => setMatomoUserId(userId),
    {immediate: true}
);

function initializeResourceTypes() {
    useEventBus('resourceTypesInitialized');
    store
        .dispatch('resources/initializeResourceTypes', {
            accountInformation: account.value,
            currentRouteName: '',
        })
        .then(() => {
            useEventBus('resourceTypesInitialized').emit();
        });
}

function useWhitelabelSettings() {
    const {subDomain} = useSubDomain();
    const accountStore = useAccountStore();
    const {data} = useWhitelabelSettingsQuery(subDomain);

    watch(
        data,
        newValue => {
            whiteLabelHelper.methods.whiteLabelCheck(newValue);

            const account = accountStore.account;
            //prevent account update when no account is set
            if (!account) {
                return;
            }

            const updatedAccount = Object.assign({}, account, newValue);
            accountStore.updateAccount(updatedAccount);
        },
        {immediate: true}
    );
}
</script>
